<script setup lang="ts">
import Draggable from 'vuedraggable';
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppSvgIcon } from '@libs/ui';

import { NightsSelector } from '../../nightsSelector';
import type { EditTripSegment } from '../models/editTripSegment';

interface EditTripSegmentsProps {
  segments: EditTripSegment[];
}

const props = defineProps<EditTripSegmentsProps>();

interface EditTripSegmentsEmits {
  (eventName: 'change-destination', segment: EditTripSegment): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', data: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (eventName: 'close'): void;
}

const emit = defineEmits<EditTripSegmentsEmits>();

const statsigEventsStore = useStatsigEventsStore();

const draggableSegments = ref<EditTripSegment[]>([]);
const draggableIndex = ref<number | undefined>(undefined);

function onEditTrip(event: { newIndex: number; oldIndex: number }): void {
  if (event.newIndex === event.oldIndex) {
    return;
  }

  resetDraggableIndex();
  emit('change-trip-order', draggableSegments.value);
}

function setDraggableIndex(event: { oldIndex: number }): void {
  draggableIndex.value = event.oldIndex;
}

function resetDraggableIndex(): void {
  draggableIndex.value = undefined;
}

function changeNumberOfNights(
  newNumberOfNights: number,
  segment: EditTripSegment
): void {
  emit('change-number-of-nights', {
    ...segment,
    numberOfNights: newNumberOfNights,
  });
}

function changeDestination(segment: EditTripSegment): void {
  emit('change-destination', segment);
}

watch(
  () => props.segments,
  () => {
    draggableSegments.value = cloneDeep(props.segments);
  },
  { immediate: true }
);
</script>

<template>
  <div class="edit-trip-segments">
    <Draggable
      v-model="draggableSegments"
      item-key="key"
      animation="200"
      handle=".drag-handle"
      drag-class="dragging-element"
      :force-fallback="true"
      :disabled="props.segments.length === 1"
      @start="setDraggableIndex"
      @unchoose="resetDraggableIndex"
      @update="onEditTrip"
    >
      <template #item="{ element, index }">
        <div class="segment">
          <div
            v-if="props.segments.length > 1"
            class="drag-handle"
            :class="{ draggable: props.segments.length > 1 }"
          >
            <AppSvgIcon
              name="drag"
              :color="
                index === draggableIndex
                  ? 'color-neutral-70'
                  : 'color-neutral-40'
              "
            />
          </div>
          <div class="details">
            <div class="destination">
              <button class="location" @click="changeDestination(element)">
                <AppSvgIcon class="icon" name="locationMark" />
                <span class="name">{{ element.destinationName }}</span>
              </button>
              <button
                class="remove-destination"
                @click="emit('remove-destination', element)"
              >
                <AppSvgIcon name="trashCanOutlined" />
              </button>
            </div>
            <div class="nights-selector">
              <NightsSelector
                :number-of-nights="element.numberOfNights"
                app-select-type="light"
                :disabled="false"
                :flicker-margin="{
                  xSpacing: 'spacing-2',
                }"
                @change-number-of-nights="changeNumberOfNights($event, element)"
                @open-number-of-nights="
                  statsigEventsStore.logCoreNightsSelectorOpened({
                    source: CoreEventSource.CarTrip,
                  })
                "
              />
            </div>
          </div>
        </div>
      </template>
    </Draggable>
  </div>
</template>

<style lang="scss" scoped>
.edit-trip-segments {
  --segment-height: 56px;

  display: flex;
  flex-direction: column;
  user-select: none;

  .segment {
    display: flex;
    align-items: flex-start;
    padding-bottom: var(--spacing-24);
    position: relative;

    .drag-handle {
      --drag-area-width: 36px;

      display: flex;
      align-items: center;

      height: 100%;
      min-width: var(--drag-area-width);
      padding-top: var(--spacing-16);

      &.draggable {
        cursor: pointer;
      }
    }

    .details {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      border: 1px solid var(--color-neutral-30);
      border-radius: var(--border-radius-8);
    }

    .destination {
      display: flex;
      align-items: center;
      flex-grow: 1;
      justify-content: space-between;
      height: 100%;
      border-bottom: 1px solid var(--color-neutral-30);

      .location {
        all: unset;
        display: flex;
        align-items: center;
        cursor: pointer;
        width: 100%;
        height: 100%;

        transition: background-color var(--transition-short);
        padding: var(--spacing-16);

        .name {
          all: unset;
          @include font-label-l-bold;
          padding-left: var(--spacing-8);
        }

        @include on-hover {
          background-color: var(--color-neutral-30);
        }
      }

      .remove-destination {
        padding: var(--spacing-16);
        border-left: 1px solid var(--color-neutral-30);
        cursor: pointer;
        color: var(--color-neutral-70);

        @include on-hover {
          background-color: var(--color-neutral-30);
          color: var(--color-neutral-70);
        }
      }
    }
    .nights-selector {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      height: 100%;
      width: 110px;
      padding: var(--spacing-8);
    }

    &:not(:first-child)::before {
      content: '';
      position: absolute;
      top: -24px;
      border: 1px solid var(--color-neutral-30);
      height: 22px;
      transform: translateX(64px);
    }

    &.dragging-element {
      background: var(--color-white);
      opacity: 1 !important;
      &::before {
        display: none;
      }
    }
  }
}
</style>
